/**
 * NetworkVideoModal — iter232
 * ────────────────────────────────────────────────────────────
 * Pulse-style bottom sheet for Featured Reel + athlete Shorts tab
 * plays. Slides up from the bottom on mobile, centered card on
 * desktop. Dark tone, grabber, autoplay-inline iframe, athlete
 * follow + profile CTAs — matches the Pulse detail sheet aesthetic
 * Jay called out. Handles BOTH horizontal (16:9 wide) and vertical
 * (9:16 short) source videos.
 *
 * Props:
 *   video    { title, subtitle, video_url, poster, body }  (required)
 *   athlete  { id, slug, name }                            (optional)
 *   variant  'wide' (16:9, default) | 'short' (9:16)
 *   onClose  () => void
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Volume2, VolumeX } from 'lucide-react';
import SaveButton from './SaveButton';
import { resolveImageUrl } from './ImageUpload';
import { toVideoEmbed } from '../utils/videoEmbed';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};
const embedUrl = (url) => {
  if (!url) return '';
  const canonical = toVideoEmbed(url) || url;
  return canonical + (canonical.includes('?') ? '&' : '?') + 'autoplay=1';
};
// iter801 · Jay: "make Mux video feel as smooth as the photo blur-up".
// iter802 · Corrected to match the ACTUAL tiers the backend requests
// (see backend/services/mux_provider.py) — explicit "480p"/"1080p"
// static renditions, not the deprecated mp4_support low/medium/high
// naming this originally assumed. Starts on "1080p" URLs by swapping
// down to the fast "480p" file for a quick first frame, then swaps back
// up a couple seconds into playback. Only matches Mux-hosted URLs —
// Cloudinary/Vimeo sources are untouched.
const muxMediumUrl = (src) => {
  if (!src || !/stream\.mux\.com\//.test(src) || !/\/1080p\.mp4(\?|$)/.test(src)) return null;
  return src.replace(/\/1080p\.mp4/, '/480p.mp4');
};
const NetworkVideoModal = ({ video, athlete, variant = 'wide', onClose }) => {
  const overlayRef = useRef(null);
  const videoElRef = useRef(null);
  const [entered, setEntered] = useState(false);
  // iter801 · Jay: the video's own mute icon (from the native `controls`
  // chrome) sat right under our custom "X" close button — both anchored
  // top-right. Native control icons can't be repositioned, so instead we
  // drive `muted` ourselves and give the athlete an unmistakable, custom
  // "tap for sound" pill at the BOTTOM-right of the frame, well clear of
  // the close button.
  const [muted, setMuted] = useState(true);
  // iter801 · Progressive-quality state — see muxMediumUrl above.
  const [videoTier, setVideoTier] = useState('medium');
  const upgradedRef = useRef(false);
  const resumeAtRef = useRef(null);
  useLayoutEffect(() => {
    if (!video) return;
    if (overlayRef.current) overlayRef.current.scrollTop = 0;
  }, [video?.video_url]);
  // Slide-up animation on mount — flips a class one tick after render
  // so the initial transform starts at translate-y-full and animates
  // to translate-y-0 for that native bottom-sheet feel.
  useEffect(() => {
    if (!video) { setEntered(false); return undefined; }
    const t = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(t);
  }, [video?.video_url]);
  // Reset mute + quality-tier state every time a different video opens.
  useEffect(() => {
    setMuted(true);
    setVideoTier('medium');
    upgradedRef.current = false;
    resumeAtRef.current = null;
  }, [video?.video_url]);
  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted((m) => {
      const next = !m;
      if (videoElRef.current) videoElRef.current.muted = next;
      return next;
    });
  };
  // A couple seconds into playback on the fast "medium" tier, swap up to
  // "high" — plenty of time for the fast start to have done its job
  // without the upgrade happening so early it defeats the point.
  const handleTimeUpdate = (e) => {
    if (upgradedRef.current || videoTier !== 'medium') return;
    if (e.currentTarget.currentTime > 2.5) {
      upgradedRef.current = true;
      resumeAtRef.current = e.currentTarget.currentTime;
      setVideoTier('high');
    }
  };
  // Swapping `src` reloads the element — restore playback position the
  // moment the new (higher-quality) file's metadata is ready so the
  // upgrade feels like a quality bump, not a restart.
  const handleLoadedMetadata = (e) => {
    if (resumeAtRef.current == null) return;
    const v = e.currentTarget;
    const resumeAt = resumeAtRef.current;
    resumeAtRef.current = null;
    v.currentTime = resumeAt;
    v.play().catch(() => {});
  };
  useEffect(() => {
    if (!video) return undefined;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, [video, onClose]);
  if (!video) return null;
  const url = video.video_url || '';
  const isDirect = url && (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) || url.startsWith('/api/files/'));
  const directSrc = isDirect ? resolveUrl(url) : null;
  const posterSrc = video.poster ? resolveImageUrl(video.poster) : null;
  const mediumSrc = muxMediumUrl(directSrc);
  const activeSrc = mediumSrc && videoTier === 'medium' ? mediumSrc : directSrc;
  // Vertical clips get a taller crop (9:16 capped so it fits within the
  // sheet on smaller phones) — horizontal gets the standard 16:9.
  const aspectStyle = variant === 'short'
    ? { aspectRatio: '9 / 16', maxHeight: '70vh' }
    : { aspectRatio: '16 / 9' };
  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="network-video-modal-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      {/* Sheet — slides up on mobile, fades in centered on desktop */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full sm:max-w-lg bg-neutral-950 ring-1 ring-white/15 shadow-2xl sm:rounded-3xl rounded-t-3xl overflow-hidden max-h-[92vh] flex flex-col transition-transform duration-300 ease-out ${entered ? 'translate-y-0' : 'translate-y-full sm:translate-y-4'}`}
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
        data-testid="network-video-modal"
      >
        {/* Grabber (mobile only) */}
        <div className="sm:hidden pt-2 pb-1 flex justify-center">
          <span className="w-10 h-1 rounded-full bg-white/25" />
        </div>
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/60 hover:bg-black/85 ring-1 ring-white/15 inline-flex items-center justify-center transition"
          data-testid="network-video-modal-close"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        {/* Video — plays instantly, no tap-to-play gate */}
        <div className="w-full bg-black flex items-center justify-center">
          {url ? (
            <div className="relative w-full flex items-center justify-center" style={aspectStyle}>
              {isDirect ? (
                <video
                  ref={videoElRef}
                  src={activeSrc}
                  controls
                  autoPlay
                  playsInline
                  /* iter361 · iOS Safari + WKWebView won't autoplay
                     with sound → mute so tapping a short starts it
                     silently and the user can unmute. Combined with
                     `preload="auto"` this shaves the "black frame
                     while buffering" delay on native. */
                  muted={muted}
                  preload="auto"
                  poster={posterSrc}
                  className="w-full h-full object-contain bg-black"
                  data-testid="network-video-modal-video"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                />
              ) : (
                <iframe
                  src={embedUrl(url)}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="network-video-modal-iframe"
                />
              )}
              {isDirect && (
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={muted ? 'Unmute video' : 'Mute video'}
                  className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 hover:bg-black/85 ring-1 ring-white/20 text-white text-[11px] font-bold transition"
                  data-testid="network-video-modal-mute"
                >
                  {muted ? <><VolumeX className="w-3.5 h-3.5" /> Tap for sound</> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-16">
              <p className="font-body text-sm">No video URL set yet</p>
            </div>
          )}
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-3">
          {athlete && (
            <p className="font-italic text-blue-300 text-[11px] tracking-[0.25em] uppercase">
              Dreams League · Network
            </p>
          )}
          <h2
            className="font-display text-2xl sm:text-3xl uppercase leading-[1.05] text-white"
            data-testid="network-video-modal-title"
          >
            {video.title}
          </h2>
          {video.subtitle && (
            <p className="font-italic text-sm text-gray-300 leading-snug">{video.subtitle}</p>
          )}
          {video.body && (
            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line line-clamp-[8]">
              {video.body}
            </p>
          )}
          {athlete && (
            <div className="mt-2 pt-4 border-t border-white/10 flex flex-wrap items-center gap-2">
              {athlete.id && (
                <SaveButton
                  type="athletes"
                  id={athlete.id}
                  variant="pill"
                  label={`Follow ${athlete.name?.split(' ')[0] || ''}`.trim()}
                  savedLabel={`Following ${athlete.name?.split(' ')[0] || ''}`.trim()}
                />
              )}
              {athlete.slug && (
                <Link
                  to={`/network/${athlete.slug}`}
                  onClick={onClose}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold bg-blue-500 hover:bg-blue-400 text-white transition"
                  data-testid="network-video-modal-profile-btn"
                >
                  Go to profile <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
export default NetworkVideoModal;
